import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  LeaveCreditBalance,
  LeaveLedgerEntry,
  LeaveLedgerEntryRequest,
  LeaveRecord,
  LeaveRecordRequest,
  LeaveType,
  LeaveTypeRequest,
} from './leaveTypes';
import type { RecruitmentUserOption } from '../vacancies/vacancyTypes';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Sent whenever the key is present, empty string included — an empty value is the reader
    saying "none of these", which matches no row. A truthiness check would drop it and the
    server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/** The leave-type catalog, maintained under Personnel → Settings → Leave Types. */
export const leaveTypeApi = {
  /** Soft-deletes every type at once; resolves with how many. The ledger is untouched. */
  clear: () => api<number>('/api/leave-types/clear', { method: 'POST' }),

  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<LeaveType>>(`/api/leave-types${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<LeaveType>(`/api/leave-types/${id}`, { signal }),

  /**
   * The ACTIVE types, in the organization's order, for the entry form's picker.
   *
   * Deleted types are left out: the form offers what can be recorded now. An entry already
   * filed against a retired type keeps it and still names it.
   */
  options: (signal?: AbortSignal) => api<LeaveType[]>('/api/leave-types/options', { signal }),

  create: (request: LeaveTypeRequest) =>
    api<LeaveType>('/api/leave-types', { method: 'POST', body: request }),

  update: (id: string, request: LeaveTypeRequest) =>
    api<LeaveType>(`/api/leave-types/${id}`, { method: 'PUT', body: request }),

  /** Soft delete: the type leaves the catalog, the entries stay on the records. */
  deactivate: (id: string) => api<void>(`/api/leave-types/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/leave-types/${id}/restore`, { method: 'POST' }),
};

/**
 * One employee's leave credits.
 *
 * Every route is scoped under the personnel record, which is what puts a write in that
 * employee's own History Logs and what lets the access-level filter guard it.
 */
export const leaveCreditApi = {
  /** One row per active leave type, whether or not this employee has an entry against it. */
  balances: (personnelId: string, signal?: AbortSignal) =>
    api<LeaveCreditBalance[]>(`/api/personnel/${personnelId}/leave-credits/balances`, { signal }),

  ledger: (personnelId: string, includeDeleted: boolean, signal?: AbortSignal) =>
    api<LeaveLedgerEntry[]>(
      `/api/personnel/${personnelId}/leave-credits/ledger${includeDeleted ? '?includeDeleted=true' : ''}`,
      { signal },
    ),

  create: (personnelId: string, request: LeaveLedgerEntryRequest) =>
    api<LeaveLedgerEntry>(`/api/personnel/${personnelId}/leave-credits/ledger`, {
      method: 'POST',
      body: request,
    }),

  update: (personnelId: string, entryId: string, request: LeaveLedgerEntryRequest) =>
    api<LeaveLedgerEntry>(`/api/personnel/${personnelId}/leave-credits/ledger/${entryId}`, {
      method: 'PUT',
      body: request,
    }),

  /** Soft delete: the entry leaves the balance and stays in the ledger. */
  deactivate: (personnelId: string, entryId: string) =>
    api<void>(`/api/personnel/${personnelId}/leave-credits/ledger/${entryId}`, { method: 'DELETE' }),

  activate: (personnelId: string, entryId: string) =>
    api<void>(`/api/personnel/${personnelId}/leave-credits/ledger/${entryId}/restore`, {
      method: 'POST',
    }),
};

/**
 * One employee's Leave History. A record that deducts writes its own Used ledger entry, so a
 * save here moves the Credit Balance and the Leave Ledger too.
 */
export const leaveRecordApi = {
  list: (personnelId: string, includeDeleted: boolean, signal?: AbortSignal) =>
    api<LeaveRecord[]>(
      `/api/personnel/${personnelId}/leave-credits/records${includeDeleted ? '?includeDeleted=true' : ''}`,
      { signal },
    ),

  create: (personnelId: string, request: LeaveRecordRequest) =>
    api<LeaveRecord>(`/api/personnel/${personnelId}/leave-credits/records`, { method: 'POST', body: request }),

  update: (personnelId: string, recordId: string, request: LeaveRecordRequest) =>
    api<LeaveRecord>(`/api/personnel/${personnelId}/leave-credits/records/${recordId}`, {
      method: 'PUT',
      body: request,
    }),

  /** Soft delete: the record and its deduction both go; the days return to the balance. */
  deactivate: (personnelId: string, recordId: string) =>
    api<void>(`/api/personnel/${personnelId}/leave-credits/records/${recordId}`, { method: 'DELETE' }),

  activate: (personnelId: string, recordId: string) =>
    api<void>(`/api/personnel/${personnelId}/leave-credits/records/${recordId}/restore`, { method: 'POST' }),

  /** The Filed by and Approved by pickers' accounts — this module's own door. */
  userOptions: (personnelId: string, signal?: AbortSignal) =>
    api<RecruitmentUserOption[]>(`/api/personnel/${personnelId}/leave-credits/user-options`, { signal }),
};
